import api from './api';
import type {
  SalesImport,
  SalesImportDetailResponse,
  SalesImportStatus,
  SalesImportUploadResponse,
} from '../types/salesImport';
import type { PaginatedResponse } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/sales-import`;

const salesImportService = {
  /** List Sales Imports for a venue (paginated). */
  list: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    status?: SalesImportStatus,
  ): Promise<PaginatedResponse<SalesImport>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (status) params.status = status;
    const res = await api.get(base(venueId), { params });
    return res.data as PaginatedResponse<SalesImport>;
  },

  /** Fetch one Sales Import with its lines. */
  get: async (
    venueId: string,
    importId: string,
  ): Promise<SalesImportDetailResponse> => {
    const res = await api.get(`${base(venueId)}/${importId}`);
    return res.data as SalesImportDetailResponse;
  },

  /** Delete a draft Sales Import. 409 if already posted. */
  remove: async (venueId: string, importId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${importId}`);
  },

  /**
   * Download the CSV template populated with one row per active POS mapping.
   * Returns a Blob — the caller triggers a browser download.
   */
  template: async (venueId: string): Promise<Blob> => {
    const res = await api.get(`${base(venueId)}/template`, { responseType: 'blob' });
    return res.data as Blob;
  },

  /** Upload + parse + preview a sales-import file. Creates a draft SalesImport. */
  upload: async (venueId: string, file: File): Promise<SalesImportUploadResponse> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`${base(venueId)}/upload`, fd, {
      // Browser must set the multipart boundary; the shared axios instance
      // defaults to application/json.
      headers: { 'Content-Type': undefined },
    });
    return res.data as SalesImportUploadResponse;
  },

  /**
   * Post a draft Sales Import. All-or-nothing — every pending line writes a
   * SALE InventoryMovement and decrements InventoryBalance in one transaction.
   * Refuses if `failedLineCount > 0` (409 SALES_IMPORT_HAS_FAILED_LINES).
   */
  post: async (
    venueId: string,
    importId: string,
  ): Promise<SalesImportDetailResponse> => {
    const res = await api.post(`${base(venueId)}/${importId}/post`);
    return res.data as SalesImportDetailResponse;
  },
};

export default salesImportService;
